import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

// Poll only visible conversations. Sequence checks reject late/overlapping reads.
export default function useMessageRefresh(endpoint, accept, enabled = true) {
  const current = useRef({ endpoint, enabled });
  current.current = { endpoint, enabled };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const sequence = useRef(0);
  const active = useRef(false);
  const controller = useRef(null);
  const load = useCallback(async () => {
    if (!active.current || !current.current.enabled || current.current.endpoint !== endpoint || document.visibilityState === "hidden") return;
    controller.current?.abort();
    const requestController = new AbortController();
    controller.current = requestController;
    const request = ++sequence.current;
    try {
      const { data } = await axios.get(endpoint, { withCredentials: true, signal: requestController.signal });
      if (!active.current || request !== sequence.current) return;
      accept(data);
      setError("");
    } catch {
      if (active.current && request === sequence.current) setError("Messages could not refresh. What you see may be out of date. Try again when you are ready.");
    } finally {
      if (active.current && request === sequence.current) setLoading(false);
    }
  }, [endpoint, accept]);
  useEffect(() => {
    active.current = enabled;
    if (!enabled) return undefined;
    setLoading(true); setError("");
    load();
    const changed = () => {
      if (document.visibilityState === "hidden") { ++sequence.current; controller.current?.abort(); }
      else load();
    };
    const timer = window.setInterval(load, 5000);
    window.addEventListener("focus", load);
    document.addEventListener("visibilitychange", changed);
    return () => {
      active.current = false; ++sequence.current; controller.current?.abort();
      window.clearInterval(timer);
      window.removeEventListener("focus", load);
      document.removeEventListener("visibilitychange", changed);
    };
  }, [load, enabled]);
  return { loading, error, load };
}
